import {
  Button,
  Tooltip,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  useToast,
  MenuItem,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  InvoiceConsolidatedDisbursementFragmentGQLType,
  InvoiceSummaryFragmentGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  GetInvoiceConsolidatedDisbursementsByInvoiceIdDocumentGQLType,
  useMarkInvoiceConsolidatedDisbursementAsPaidMutation,
  useUndoMarkInvoiceConsolidatedDisbursementAsPaidMutation,
} from 'packages/invoices/requests/invoices.gql.generated';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';

interface MarkAsDisbursedModalProps {
  invoiceConsolidatedDisbursement: InvoiceConsolidatedDisbursementFragmentGQLType;
  invoicePaymentDate: Util.FullDateStr;
  modalControls: ReturnType<typeof useDisclosure>;
  paymentAction: 'Disbursed' | 'Notified';
  invoice: InvoiceSummaryFragmentGQLType;
}

const MarkAsDisbursedModal = ({
  invoiceConsolidatedDisbursement,
  invoicePaymentDate,
  modalControls,
  paymentAction,
  invoice,
}: MarkAsDisbursedModalProps) => {
  const toast = useToast();
  const [disbursementDate, setDisbursementDate] = useState<Date | null>(
    invoicePaymentDate
      ? commonDate.fullDateStrToDate(invoicePaymentDate) // util uses client tz default
      : new Date(),
  );
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isCapitalizationOnlyInvoice = useMemo(() => {
    return invoice.invoiceComponentsWithInvoiceItems.every((component) =>
      component.invoiceItems.every((item) => !!item.capitalizedLoanId),
    );
  }, [invoice.invoiceComponentsWithInvoiceItems]);

  const [
    markAsDisbursed,
    { loading: isLoading },
  ] = useMarkInvoiceConsolidatedDisbursementAsPaidMutation({
    refetchQueries: [
      GetInvoiceConsolidatedDisbursementsByInvoiceIdDocumentGQLType,
    ],
  });

  const handleMarkAsDisbursedClick = async () => {
    try {
      if (disbursementDate) {
        await markAsDisbursed({
          variables: {
            invoiceConsolidatedDisbursementId:
              invoiceConsolidatedDisbursement.id,
            paymentDate: commonDate.dateToFullDateString(
              disbursementDate,
              clientTimeZone,
            ),
          },
        });
        toast({
          ...successToastConfig,
          title: `Disbursement notice marked as disbursed for ${invoiceConsolidatedDisbursement.capitalProviderOrganization.name}`,
        });
      }
    } catch (error) {
      toast({
        ...errorToastConfig,
        title:
          'If you are unable to resolve the error, contact the Finley support team for assistance.',
      });
    }
  };

  return (
    <Modal {...modalControls}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Typography variant="section-subtitle">
            Select {paymentAction} Date
          </Typography>
          <Typography variant="base-muted">
            This date will be saved as the &apos;{paymentAction} Date&apos;.
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleDatePicker
            dateFormat="MMMM dd, yyyy"
            // allow disbursement date to be set for any day for capitalization only invoices, since it doesn't include cash movement
            excludeHolidays={!isCapitalizationOnlyInvoice}
            excludeWeekends={!isCapitalizationOnlyInvoice}
            minDate={commonDate.fullDateStrToDate(invoicePaymentDate)}
            selectedDate={disbursementDate}
            setSelectedDate={setDisbursementDate}
          />
        </ModalBody>
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            colorScheme="v2-aurora"
            isDisabled={!disbursementDate}
            onClick={async () => {
              await handleMarkAsDisbursedClick();
              modalControls.onClose();
            }}
            variant="primary"
            isLoading={isLoading}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const MarkAsDisbursedButton = ({
  invoiceConsolidatedDisbursement,
  paymentAction,
  invoice,
}: {
  invoiceConsolidatedDisbursement: InvoiceConsolidatedDisbursementFragmentGQLType;
  paymentAction: 'Disbursed' | 'Notified';
  invoice: InvoiceSummaryFragmentGQLType;
}) => {
  const modalControls = useDisclosure();

  return (
    <>
      <Tooltip
        label={
          invoice.paymentDate
            ? undefined
            : 'Invoice must be paid to mark disbursement as disbursed'
        }
      >
        <Button
          aria-label="mark disbursed"
          onClick={modalControls.onOpen}
          variant="secondary"
          colorScheme="v2-aurora"
          isDisabled={!invoice.paymentDate}
        >
          Mark {paymentAction}
        </Button>
      </Tooltip>
      {invoice.paymentDate && (
        <MarkAsDisbursedModal
          invoiceConsolidatedDisbursement={invoiceConsolidatedDisbursement}
          modalControls={modalControls}
          invoicePaymentDate={invoice.paymentDate}
          paymentAction={paymentAction}
          invoice={invoice}
        />
      )}
    </>
  );
};

export const UndoMarkAsDisbursedButton = ({
  invoiceConsolidatedDisbursementId,
  paymentAction,
}: {
  invoiceConsolidatedDisbursementId: string;
  paymentAction: 'Disbursed' | 'Notified';
}) => {
  const [
    undoMarkAsDisbursed,
    { loading: isLoading },
  ] = useUndoMarkInvoiceConsolidatedDisbursementAsPaidMutation({
    refetchQueries: [
      GetInvoiceConsolidatedDisbursementsByInvoiceIdDocumentGQLType,
    ],
  });

  const handleOnClickUndoMarkAsDisbursed = async () => {
    await undoMarkAsDisbursed({
      variables: {
        invoiceConsolidatedDisbursementId,
      },
    });
  };

  return (
    <MenuItem as="div">
      <Button
        variant="ghost"
        colorScheme="gray"
        fontWeight={400}
        onClick={handleOnClickUndoMarkAsDisbursed}
        isLoading={isLoading}
      >
        Undo Mark {paymentAction}
      </Button>
    </MenuItem>
  );
};
